import { useCalendarTime } from "components/Dashboard/DoctorDashboard/views/one-one-sessions/contexts/CalendarTimeContext";
import { DAYS_OF_WEEK } from "constants/daysOfWeek";
import moment from "moment-timezone";
import { useEffect, useRef, useState } from "react";
import { AmplitudePageName } from "utils/amplitude";
import styles from "./MonthlyView.module.scss";
import EntryCell from "./components/entry-cell/EntryCell";
import useMonthlyViewEventSlots from "./hooks/use-monthly-view-event-slots";
import useScheduleIntervalsByDay from "./hooks/use-schedule-intervals-by-day";
import { SINGLE_DAY_DATE_FORMAT } from "./monthly-view.types";

const HEADER_ROW_HEIGHT = 40;

type Props = {
  amplitudePageName: AmplitudePageName;
};

const MonthlyView = ({ amplitudePageName }: Props) => {
  const { currentCalendarTimezone, anchorTime } = useCalendarTime();
  const containerRef = useRef<HTMLTableElement | null>(null);
  const [bodyCellHeight, setBodyCellHeight] = useState(100);

  const today = moment().tz(currentCalendarTimezone);

  const scheduleIntervalsByDay = useScheduleIntervalsByDay();
  const eventSlots = useMonthlyViewEventSlots();

  useEffect(() => {
    if (!containerRef.current) {
      return;
    }

    const el = containerRef.current.parentElement;
    if (!el) {
      return;
    }
    // add resize listener
    const handler = () => {
      // minus header row and border and padding
      const tbodyHeight = el.clientHeight - HEADER_ROW_HEIGHT - 6 - 6;
      setBodyCellHeight(tbodyHeight / 5);
    };
    // run the handler first
    handler();
    window.addEventListener("resize", handler);
    return () => window.removeEventListener("resize", handler);
  }, [containerRef]);

  return (
    <table className={styles["monthly-view"]} ref={containerRef}>
      <thead>
        <tr className={styles["monthly-view__header-row"]}>
          {Array.from({ length: 7 }).map((_, index) => (
            <th
              key={DAYS_OF_WEEK[index]}
              className={styles["monthly-view__header-cell"]}
            >
              {DAYS_OF_WEEK[index]}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {eventSlots.map((week) => (
          <tr key={week[0].startISO}>
            {week.map((day) => {
              const startValue = new Date(day.startISO).valueOf();
              const startMoment = moment.tz(
                startValue,
                currentCalendarTimezone,
              );
              const dateId = startMoment.format(SINGLE_DAY_DATE_FORMAT);
              const isToday = startMoment.isSame(today, "d");
              const isSameMonth = startMoment.isSame(anchorTime, "month");

              return (
                <EntryCell
                  key={day.startISO}
                  isSameMonth={isSameMonth}
                  isToday={isToday}
                  height={bodyCellHeight}
                  startValue={startValue}
                  slots={day.slots}
                  hasScheduleIntervals={scheduleIntervalsByDay[dateId]}
                  amplitudePageName={amplitudePageName}
                />
              );
            })}
          </tr>
        ))}
      </tbody>
    </table>
  );
};

export default MonthlyView;
